import { Box, Text } from '@chakra-ui/react';
import { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import type { Column } from 'react-table';
import Datatable from '@/components/common/Datatable';
import { formatApiDateToDisplay } from '@/utils/dateUtils';
import { useGetCustomerPaymentHistory } from '@/services/payment';

interface Order {
  order_id: string;
  payment_date: string;
  transaction_id: string;
  customer_name: string;
  vendor_revenue_usd: number;
  payment_method: string;
  status: string;
  currency: string;
}

const CustomerVendorPayment: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const vendor_pk = vendorId ? parseInt(vendorId, 10) : undefined;

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  });
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<{
    from_date?: string;
    to_date?: string;
  }>({});

  const { data, isLoading, isError, refetch } = useGetCustomerPaymentHistory({
    page: pagination.page,
    page_size: pagination.pageSize,
    search: query || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
    vendor_id: vendor_pk,
  });

  const [tableData, setTableData] = useState<Order[]>([]);

  useEffect(() => {
    if (data?.results) {
      const transformed = data.results.map((item) => ({
        order_id: item.parent_transaction_id,
        payment_date: item.created_at.split(' ')[0] || 'N/A',
        transaction_id: item.parent_transaction_id,
        customer_name:
          `${item.customer.first_name || ''} ${item.customer.last_name || ''}`.trim() || 'N/A',
        vendor_revenue_usd: parseFloat(item.vendor_revenue_usd) || 0,
        payment_method: 'N/A',
        status: 'Paid',
        currency: item.currency || 'USD',
      }));

      setTableData(transformed);

      setPagination((prev) => ({
        ...prev,
        pageSize: data.pageSize || 10,
        totalCount: data.count || 0,
        totalPages: Math.max(1, data.page_count || 1),
      }));

      if (data.page_count && pagination.page > data.page_count && data.page_count > 0) {
        setPagination((p) => ({ ...p, page: data.page_count! }));
      }
    }
  }, [data, pagination.page]);

  const handleSearch = (value: string) => {
    setQuery(value);
    setPagination((p) => ({ ...p, page: 1 }));
    refetch();
  };

  const applyFilters = () => {
    setPagination((p) => ({ ...p, page: 1 }));
    refetch();
  };

  const resetFilters = () => {
    setFilters({ from_date: '', to_date: '' });
    setPagination((p) => ({ ...p, page: 1 }));
    refetch();
  };

  const handlePageChange = (page: number) => {
    setPagination((p) => ({ ...p, page }));
  };

  const columns: Column<Order>[] = useMemo(
    () => [
      {
        Header: 'Payment Date',
        accessor: 'payment_date',
        Cell: ({ value }) => (
          <Text fontWeight="medium" color="text.primary">
            {formatApiDateToDisplay(value)}
          </Text>
        ),
      },
      {
        Header: 'Transaction ID',
        accessor: 'transaction_id',
        Cell: ({ value }) => (
          <Text fontWeight="medium" color="text.primary">
            {value}
          </Text>
        ),
      },
      {
        Header: 'Customer Name',
        accessor: 'customer_name',
        Cell: ({ value }) => (
          <Text fontWeight="medium" color="text.primary">
            {value}
          </Text>
        ),
      },
      {
        Header: 'Amount Paid',
        accessor: 'vendor_revenue_usd',
        Cell: ({ row }) => (
          <Text fontWeight="medium" color="text.primary" w="100px">
            ${row.original.vendor_revenue_usd.toFixed(2)}
          </Text>
        ),
      },
    ],
    [],
  );
  return (
    <Box bg="background.white">
      <Datatable<Order>
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        isError={isError}
        onSearch={handleSearch}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        hideCategoryFilter={true}
        hideFilters={false}
        dateFilterType="from_to"
        restrictDateRange={true}
      />
    </Box>
  );
};

export default CustomerVendorPayment;
